import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { generateJobUrl, Locale, toRelativePath } from '../../../utils/routing/generate-url';
import { BASE_URL, DEV_URL } from '../../../utils/constants/url-constants';
import { usePushIndexUiState } from '../../../hooks/usePushIndexUiState';
import { useSearchFilters, useSearchQuery, useSearchPage } from '../../../contexts/SearchContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../hooks/useAuth';
import { useBrowsedJobTracking } from '../../../hooks/useBrowsedJobTracking';
import { useAppSelector } from '../../../redux/hooks';
import { calculateJobMatchScore } from '../../../utils/data/calculate-job-match-score';
import { trackJobClick, trackJobShare } from '../../../utils/analytics/analytics';
import { sharePage as sharePageUtil } from '../../../pages/job/@jobId/@seoSlug/utils/shareUtils';
import { useHitState } from './useHitState';
import { useHitActions } from './useHitActions';
import { getBrandColorStyles, TYOMARKKINATORI_PURPLE } from '../utils/brandColorUtils';
import { getLocationDisplayWithCount, getSinglePrioritizedLocation } from '../utils';
import { useApplicationStateButtonState, APPLICATION_STATES } from '../../application-state';
import type { DisplayJob } from '../../../types';
import type { ApplicationState } from '../../../redux/features/jobs/jobsSlice';

export interface UseHitSharedProps {
  hit: DisplayJob;
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
}

export interface HitSharedData {
  jobId: string;
  jobUrl: string;
  articleId: string;
  titleId: string;
  jobTitle: string;
  isSaved: boolean;
  isViewed: boolean;
  isApplied: boolean;
  isHidden: boolean;
  currentApplicationState: ApplicationState | null;
  isToolsPage: boolean;
  savedJobPayload: DisplayJob & { id: string };
  brandColour?: string;
  useBrandColour: boolean;
  leftBorderStyle: React.CSSProperties;
  matchScore: number | null;
  matchScoreData: any;
  onLoginClick: () => void;
  actions: ReturnType<typeof useHitActions> & { handleShare: () => Promise<void> };
  getUrlForIndexUiState: (state: { query: string; refinementList: Record<string, string[]>; page: number }) => string;
  pushIndexUiState: (state: { query: string; refinementList: Record<string, string[]>; page: number }) => void;
  makeRefinementState: (facet: string, value: string | string[]) => { query: string; refinementList: Record<string, string[]>; page: number };
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void;
  browsedJobRef: React.RefObject<HTMLElement>;
  menuRefDesktop: React.RefObject<HTMLDivElement>;
  menuRefMobile: React.RefObject<HTMLDivElement>;
  /** Which ellipses button opened the menu; null when closed. Used so only one dropdown is rendered (desktop vs mobile). */
  menuOpenSource: 'desktop' | 'mobile' | null;
  setMenuOpenSource: (source: 'desktop' | 'mobile' | null) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  buttonState: { label: string; icon: React.ComponentType<{ className?: string }>; color: string } | null;
  locationData: ReturnType<typeof getLocationDisplayWithCount>;
  locationStr: string;
  filters: any;
  hit: DisplayJob;
  sendEvent?: (eventType: string, hit: DisplayJob, eventName?: string, payload?: object) => void;
}

/**
 * Shared hook for Hit component logic
 * Consolidates state, actions, and utilities used by both card and compact variants
 */
export type MenuOpenSource = 'desktop' | 'mobile' | null;

export function useHitShared({ hit, sendEvent }: UseHitSharedProps): HitSharedData {
  const intl = useIntl();
  const [menuOpenSource, setMenuOpenSource] = useState<MenuOpenSource>(null);
  const menuRefDesktop = useRef<HTMLDivElement>(null);
  const menuRefMobile = useRef<HTMLDivElement>(null);
  const isMenuOpen = menuOpenSource !== null;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDesktop = !menuRefDesktop.current?.contains(target);
      const isOutsideMobile = !menuRefMobile.current?.contains(target);
      
      // Check if click is inside the portal menu (menu is rendered via portal)
      const clickedElement = event.target as HTMLElement;
      const isInsidePortalMenu = clickedElement.closest('[data-application-state-menu="true"]') !== null;
      
      if (isOutsideDesktop && isOutsideMobile && !isInsidePortalMenu) {
        setMenuOpenSource(null);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);
  
  // Get state
  const state = useHitState({ hit });
  const { jobId, isSaved, isViewed, isApplied, isHidden, currentApplicationState, isToolsPage, savedJobPayload } = state;
  
  // Get toast
  let showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void = () => {};
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
  }
  
  // Get actions
  const actions = useHitActions({
    hit,
    jobId,
    isSaved,
    isViewed,
    isApplied,
    isHidden,
    savedJobPayload,
    showToast,
    onMenuClose: () => setMenuOpenSource(null),
  });
  
  // Get search utilities
  const { getUrlForIndexUiState, pushIndexUiState } = usePushIndexUiState();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const { page } = useSearchPage();
  
  // Make refinement state helper
  const makeRefinementState = useCallback((facet: string, value: string | string[]): { query: string; refinementList: Record<string, string[]>; page: number } => {
    const currentRefinements = (filters as Record<string, string[]>) || {};
    const preserved = { ...currentRefinements };
    if (facet === 'jobType') {
      delete preserved['jobType'];
    }
    if (facet === 'vacationWeeks') {
      delete preserved['vacationWeeks'];
    }
    const singleValue = Array.isArray(value) ? value[0] : value;
    return {
      query: '',
      refinementList: {
        ...preserved,
        [facet]: [singleValue],
      },
      page: 0,
    };
  }, [filters]);
  
  // Current pathname: use Vike pageContext so SSR and client render the same returnTo (avoids hydration mismatch)
  let pageContext: ReturnType<typeof usePageContext> | null = null;
  try {
    pageContext = usePageContext();
  } catch {
    // PageContext not available (e.g. outside Vike)
  }
  const currentPathname = pageContext
    ? ((pageContext as { urlPathname?: string }).urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : undefined))
    : (typeof window !== 'undefined' ? window.location.pathname : undefined);

  // Generate job URL; when on SRP, append returnTo so breadcrumb on job detail can link back to this search
  const jobUrl = useMemo(() => {
    const locationStr = Array.isArray(hit.location) && hit.location.length > 0
      ? String(hit.location[0] || '')
      : String(hit.location || '');
    
    const url = generateJobUrl({
      baseUrl: import.meta.env?.DEV ? DEV_URL : BASE_URL,
      jobId: hit.objectID.toString(),
      locale: intl.locale as Locale,
      title: hit.normalisedTitle,
      company: hit.companyId ?? '',
      location: locationStr,
      remoteness: String(hit.remoteness || ''),
    });
    let relative = toRelativePath(url);
    const currentSearchPath = getUrlForIndexUiState(
      {
        query: query ?? '',
        refinementList: (filters as Record<string, string[]>) ?? {},
        page: page ?? 0,
      },
      currentPathname
    );
    if (currentSearchPath) {
      const separator = relative.includes('?') ? '&' : '?';
      relative += `${separator}returnTo=${encodeURIComponent(currentSearchPath)}`;
    }
    return relative;
  }, [hit, intl.locale, getUrlForIndexUiState, query, filters, page, currentPathname]);
  
  // Get location data
  const locationData = useMemo(() => getLocationDisplayWithCount(hit), [hit]);
  const locationStr = useMemo(() => {
    if (Array.isArray(hit.location) && hit.location.length > 0) {
      return String(hit.location[0] || '');
    }
    return String(hit.location || '');
  }, [hit]);
  
  // Brand color styles (effective colour so Details button and border match SSR)
  const brandColour = hit.isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : hit.brandColour;
  const brandStyles = useMemo(() => getBrandColorStyles(brandColour, hit.isTyomarkkinatori), [brandColour, hit.isTyomarkkinatori]);
  
  // Match score
  const { isAuthenticated, signIn } = useAuth();
  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const userStateSlice = useAppSelector((state) => state.userState);
  const isHydrated = userStateSlice.hydratedFromSSR || (typeof window !== 'undefined');
  
  const matchScoreData = useMemo(() => {
    if (!isAuthenticated || !isHydrated) return null;
    return calculateJobMatchScore(hit, userProfile, comparisonPreferences);
  }, [isAuthenticated, isHydrated, hit, userProfile, comparisonPreferences]);
  
  const matchScore = matchScoreData?.overall ?? null;
  
  const handleLoginClick = useCallback(() => {
    signIn();
  }, [signIn]);
  
  // Track browsed jobs
  const browsedJobRef = useBrowsedJobTracking(
    savedJobPayload,
    async () => {}, // recordBrowsedJob - handled internally by hook
    isAuthenticated && !isToolsPage
  );
  
  // Application state button state
  const buttonState = useApplicationStateButtonState(currentApplicationState, isApplied);
  
  // Share handler
  const handleShare = useCallback(async () => {
    trackJobShare({
      jobId: hit.objectID,
      jobTitle: hit.normalisedTitle || hit.title || '',
      locale: intl.locale,
    });
    
    const url = generateJobUrl({
      baseUrl: import.meta.env?.DEV ? DEV_URL : BASE_URL,
      jobId: hit.objectID.toString(),
      locale: intl.locale as Locale,
      title: hit.normalisedTitle,
      company: hit.companyId ?? '',
      location: locationStr,
      remoteness: String(hit.remoteness || ''),
    });
    
    const jobTitle = hit.normalisedTitle || hit.title || document.title;
    
    await sharePageUtil(
      url,
      jobTitle,
      () => {
        showToast(
          intl.formatMessage({
            id: 'hit.linkCopied',
            defaultMessage: 'Link copied to clipboard!',
            description: 'Message shown when link is copied to clipboard',
          }),
          'success'
        );
      },
      (error) => {
        showToast(
          intl.formatMessage({
            id: 'hit.shareError',
            defaultMessage: 'Something went wrong while trying to share this job.',
            description: "Message shown when there's an error sharing",
          }),
          'error'
        );
      }
    );
  }, [hit, intl, locationStr, showToast]);
  
  // Add share to actions
  const actionsWithShare = {
    ...actions,
    handleShare,
  };
  
  // IDs
  const articleId = `job-${hit.objectID}`;
  const titleId = `${articleId}-title`;
  const jobTitle = hit.normalisedTitle || hit.title || intl.formatMessage({ id: 'hit.jobTitle', defaultMessage: 'Job', description: 'Fallback text when job title is not available, used as placeholder' });
  
  return {
    jobId,
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isViewed,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage,
    savedJobPayload,
    brandColour,
    useBrandColour: brandStyles.useBrandColour,
    leftBorderStyle: brandStyles.leftBorderStyle,
    matchScore,
    matchScoreData,
    onLoginClick: handleLoginClick,
    actions: actionsWithShare,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    showToast,
    browsedJobRef: browsedJobRef as React.RefObject<HTMLElement>,
    menuRefDesktop,
    menuRefMobile,
    menuOpenSource,
    setMenuOpenSource,
    isMenuOpen,
    setIsMenuOpen: (open: boolean) => setMenuOpenSource(open ? 'desktop' : null),
    buttonState,
    locationData,
    locationStr,
    filters,
    hit,
    sendEvent,
  };
}
